<script setup lang="ts">
import type { JournalListItemDTO } from '~/shared/api/client/journals/journals.dto';
import JournalCardsGrid from './JournalCardsGrid.vue';

/**
 * The grid of a tab: the cards it has been given, plus "Загрузить еще" while
 * the endpoint has more. The portions are the server's — 839 issues never
 * reach the client — so this only draws what it is handed and asks for the
 * next page.
 *
 * The heading above it belongs to the page: "Журналы" writes "Другие выпуски"
 * over the issues that follow the hero, "Архив" has no hero and no heading
 */
const props = defineProps<{
  issues: JournalListItemDTO[];
  hasMore: boolean;
  isLoading?: boolean;
}>();

const emit = defineEmits<{ loadMore: [] }>();

/**
 * The grid has two columns, and a portion is eight issues — less the one the
 * hero took on "Журналы", so every portion there ended with an empty cell. While
 * more are coming the odd card waits for the next portion and opens it; only
 * the very end of the list may leave a cell empty
 */
const shownIssues = computed(() =>
  props.hasMore && props.issues.length % 2
    ? props.issues.slice(0, -1)
    : props.issues,
);
</script>

<template>
  <div :class="$style.root">
    <JournalCardsGrid :issues="shownIssues" />
    <div v-if="hasMore" :class="$style.more">
      <Button
        theme="secondary"
        size="large"
        :is-loading="isLoading"
        @click="emit('loadMore')"
      >
        Загрузить еще
      </Button>
    </div>
  </div>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  padding-bottom: var(--space-3x);
}

.more {
  display: flex;
  justify-content: center;
  // Same distance above the button as the section leaves below it
  padding-top: var(--space-3x);
}

@media (max-width: 767px) {
  /*
   * The button stretches to the full width of its box on a phone, so the box
   * keeps the same 16px gutter the cards above are drawn in — otherwise it
   * runs edge to edge while the grid does not
   */
  .more {
    padding-left: var(--space-2x);
    padding-right: var(--space-2x);
  }
}
</style>
